import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import Layout from '../components/Layout.jsx';
import { api } from '../api.js';

const fmt = (n) => '$ ' + new Intl.NumberFormat('es-CO').format(n ?? 0);
const TIPOS = [
  { v: 'venta', l: 'Venta', icon: '💸', desc: 'Cobro al huésped' },
  { v: 'cortesia', l: 'Cortesía', icon: '☕', desc: 'Reunión interna' },
  { v: 'merma', l: 'Merma', icon: '🚯', desc: 'Dañado o vencido' },
];

export default function Movimientos() {
  const [params] = useSearchParams();
  const tipoInicial = params.get('nuevo') ?? 'venta';

  const [turno, setTurno] = useState(null);
  const [productos, setProductos] = useState([]);
  const [unidades, setUnidades] = useState([]);
  const [movs, setMovs] = useState([]);
  const [form, setForm] = useState({ tipo: tipoInicial, producto_id: '', unidad_id: '', cantidad: 1, observacion: '' });
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState('');

  const cargar = async () => {
    const [t, p, u] = await Promise.all([api.turnoAbierto(), api.productos(), api.unidades()]);
    setTurno(t.turno);
    setProductos(p.productos);
    setUnidades(u.unidades);
    if (t.turno) {
      const m = await api.movimientos(t.turno.id);
      setMovs(m.movimientos);
    }
  };

  useEffect(() => { cargar(); }, []);

  const productoSel = productos.find((p) => p.id === Number(form.producto_id));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMsg('');
    try {
      const monto = form.tipo === 'venta' ? (productoSel?.precio_venta ?? 0) * Number(form.cantidad) : 0;
      await api.crearMovimiento({
        turno_id: turno?.id ?? null,
        unidad_id: form.unidad_id ? Number(form.unidad_id) : null,
        producto_id: Number(form.producto_id),
        cantidad: Number(form.cantidad),
        tipo: form.tipo,
        monto,
        observacion: form.observacion || null,
      });
      setMsg('✓ Registrado.');
      setForm({ ...form, cantidad: 1, observacion: '' });
      await cargar();
      setTimeout(() => setMsg(''), 2500);
    } catch (err) {
      setMsg('Error: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout>
      <div className="mb-8">
        <p className="eyebrow mb-2">Operación</p>
        <h1 className="text-4xl md:text-5xl font-bold tracking-tight">Movimientos</h1>
        <p className="text-muted mt-3 max-w-xl">Registra ventas a huéspedes, cortesías de oficina y mermas. Todo queda atado al turno abierto.</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        <form onSubmit={submit} className="card lg:col-span-2 space-y-5 h-fit">
          <div className="flex items-center gap-2 mb-1">
            <span className="pill-ink">Nuevo</span>
            {turno ? (
              <span className="pill-lime">turno abierto</span>
            ) : (
              <span className="pill">sin turno</span>
            )}
          </div>

          <div>
            <label className="label">Tipo</label>
            <div className="grid grid-cols-3 gap-2">
              {TIPOS.map((t) => (
                <button
                  key={t.v}
                  type="button"
                  onClick={() => setForm({ ...form, tipo: t.v })}
                  className={`px-3 py-3 rounded-2xl text-sm font-medium tracking-tight transition-all duration-150 flex flex-col items-center gap-1 ${
                    form.tipo === t.v
                      ? 'bg-ink text-paper shadow-[0_8px_20px_-8px_rgba(27,42,78,0.5)]'
                      : 'bg-bone text-ink hover:bg-line'
                  }`}
                >
                  <span className="text-xl">{t.icon}</span>
                  <span>{t.l}</span>
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="label">Producto</label>
            <select className="input" value={form.producto_id} onChange={(e) => setForm({ ...form, producto_id: e.target.value })} required>
              <option value="">— elegir —</option>
              {productos.map((p) => (
                <option key={p.id} value={p.id}>{p.nombre} {p.precio_venta > 0 ? `· ${fmt(p.precio_venta)}` : ''}</option>
              ))}
            </select>
          </div>

          {form.tipo === 'venta' && (
            <div>
              <label className="label">Habitación / unidad</label>
              <select className="input" value={form.unidad_id} onChange={(e) => setForm({ ...form, unidad_id: e.target.value })}>
                <option value="">— elegir —</option>
                {unidades.map((u) => (
                  <option key={u.id} value={u.id}>{u.codigo} · {u.nombre}</option>
                ))}
              </select>
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="label">Cantidad</label>
              <input className="input text-center text-lg font-semibold" type="number" min={1} value={form.cantidad} onChange={(e) => setForm({ ...form, cantidad: e.target.value })} required />
            </div>
            <div>
              <label className="label">Total</label>
              <div className="rounded-2xl bg-bone px-4 py-3 font-mono text-lg font-semibold text-ink text-center">
                {productoSel && form.tipo === 'venta' ? fmt((productoSel.precio_venta ?? 0) * Number(form.cantidad)) : '—'}
              </div>
            </div>
          </div>

          <div>
            <label className="label">Observación (opcional)</label>
            <input className="input" value={form.observacion} onChange={(e) => setForm({ ...form, observacion: e.target.value })} placeholder="ej. junta semanal, cliente VIP…" />
          </div>

          {msg && (
            <div className={`rounded-2xl px-4 py-3 text-sm font-mono ${msg.startsWith('✓') ? 'bg-lime/30 text-ink' : 'bg-red-50 text-red-800 border border-red-200'}`}>
              {msg}
            </div>
          )}

          <button className="btn-primary w-full" disabled={loading}>
            {loading ? 'Registrando…' : 'Registrar →'}
          </button>
        </form>

        <div className="lg:col-span-3 card">
          <div className="flex items-center justify-between mb-5">
            <div>
              <p className="eyebrow mb-1">Bitácora del turno</p>
              <h2 className="text-xl font-semibold tracking-tight">Últimos movimientos</h2>
            </div>
            {movs.length > 0 && (
              <span className="pill">{movs.length} reg.</span>
            )}
          </div>
          {!turno && <p className="text-muted text-sm">Abre un turno desde el dashboard antes de registrar movimientos.</p>}
          {turno && movs.length === 0 && <p className="text-muted text-sm">Sin movimientos aún. Registra el primero a la izquierda.</p>}
          <div className="space-y-2">
            {movs.map((m) => (
              <div key={m.id} className="flex items-center justify-between gap-3 py-3 px-4 rounded-2xl hover:bg-bone transition border border-transparent hover:border-line/60">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="text-xl shrink-0">
                    {m.tipo === 'venta' ? '💸' : m.tipo === 'cortesia' ? '☕' : m.tipo === 'merma' ? '🚯' : '📦'}
                  </div>
                  <div className="min-w-0">
                    <div className="font-medium tracking-tight truncate">{m.producto}</div>
                    <div className="font-mono text-[10px] uppercase tracking-[0.16em] text-muted mt-0.5">
                      {m.tipo} · {m.cantidad}ud {m.unidad ? `· ${m.unidad}` : ''} {m.usuario ? `· ${m.usuario}` : ''}
                    </div>
                  </div>
                </div>
                <div className={`font-mono text-sm shrink-0 ${m.tipo === 'venta' ? 'text-ink font-semibold' : 'text-muted'}`}>
                  {m.monto > 0 ? fmt(m.monto) : '—'}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </Layout>
  );
}
